import type { AxiosError } from 'axios'
import type { InsuranceParamsDto, InsuranceParamsPropertyDto, InsuranceParamsTravelDto } from 'BrokerTsLib/src/dtos'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { WithAuth } from '@/containers/hoc/WithAuth'

import { useCreateInsuranceParamsFromDraft } from '@/hooks/useInsuranceDraft'
import { Footer } from '../components/Footer'
import { Header } from '../components/Header'
import { AppProvider } from '../context/AppContext'
import { useApp } from '../hooks/useApp'
import { useGetInsuranceParams, useGetInsuranceParams as useGetInsuranceParamsTravel } from '../hooks/useWizard'
import { useGetInsuranceParamsAddon } from '../hooks/useWizardAddon'
import type { IAvailableApps } from '../utils/availableApps'

export type insuranceParamsContextProps = {
  isLoadingData: boolean
  isLoading: boolean
  insuranceParamsId: string | undefined
  refetchInsuranceParams: () => Promise<any>
  insuranceParams?: InsuranceParamsDto | InsuranceParamsTravelDto | InsuranceParamsPropertyDto
  triggerNewInsurance: boolean
  clearTriggerNewInsurance: () => void
  error?: AxiosError<any>
}
export const insuranceParamsContext = createContext<insuranceParamsContextProps>({} as insuranceParamsContextProps)

export interface WizzardLayoutProps {
  app: IAvailableApps['appId']
}

const WizardLayoutContent = () => {
  const { app } = useApp()

  const { insuranceParamsId: insuranceParamsRd_Params } = useParams()

  // potrebuji vynutit prepsani zmeny v kontextu (napr. stare id zustavalo kdyz se z K1 kliklo na 'nove pojisteni')
  const [paramsId, setParamsId] = useState(insuranceParamsRd_Params)
  useEffect(() => {
    setParamsId(insuranceParamsRd_Params)
  }, [insuranceParamsRd_Params])

  const { isLoading: isLoadingVeh, isFetching: isFetchingVeh, data: step1DataVeh, refetch: refetchVeh, error: errorVeh } = useGetInsuranceParams(paramsId ?? '', { enabled: false, cacheTime: 0 })
  const { isLoading: isLoadingTra, isFetching: isFetchingTra, data: step1DataTra, refetch: refetchTra, error: errorTra } = useGetInsuranceParamsTravel(paramsId ?? '', { enabled: false, cacheTime: 0 })
  const { isLoading: isLoadingAdd, isFetching: isFetchingAdd, data: step1DataAdd, refetch: refetchAdd, error: errorAdd } = useGetInsuranceParamsAddon(paramsId ?? '', { enabled: false, cacheTime: 0 })

  let isLoading = isLoadingVeh
  let isFetching = isFetchingVeh
  let step1Data: any = step1DataVeh
  let refetch: any = refetchVeh
  let error: any = errorVeh

  if (app === 'carkulka-extra') {
    isLoading = isLoadingAdd
    isFetching = isFetchingAdd
    step1Data = step1DataAdd
    refetch = refetchAdd
    error = errorAdd
  }
  else if (app === 'cestovko') {
    isLoading = isLoadingTra
    isFetching = isFetchingTra
    step1Data = step1DataTra
    refetch = refetchTra
    error = errorTra
  }

  const [searchParams, setSearchParams] = useSearchParams()

  // nacteni infa o nabidce
  useEffect(() => {
    if (paramsId)
      refetch()
  }, [paramsId])

  const { mutateAsync: createInsuranceParamsFromDraftAsync } = useCreateInsuranceParamsFromDraft()
  const createInsuranceParamsFromDraftCalled = useRef(false)

  const navigate = useNavigate()
  useEffect(() => {
    const draft = searchParams.get('draft')
    if (draft && !createInsuranceParamsFromDraftCalled.current) {
      createInsuranceParamsFromDraftCalled.current = true
      createInsuranceParamsFromDraftAsync(draft)
        .then((response) => {
          setParamsId(response.data.toString())
          navigate(`1/${response.data}`)
        })
    }
  }, [searchParams, createInsuranceParamsFromDraftAsync])

  return (
    <insuranceParamsContext.Provider value={{
      insuranceParamsId: paramsId,
      isLoadingData: isLoading || isFetching,
      isLoading: isLoading,
      refetchInsuranceParams: refetch,
      insuranceParams: step1Data?.data,
      clearTriggerNewInsurance: () => {
        searchParams.delete('new')
        setSearchParams(searchParams)
      },
      triggerNewInsurance: searchParams.get('new') !== null,
      error: error
    }}>
      <Header />

      <Outlet />

      <Footer />
    </insuranceParamsContext.Provider>
  )
}

export const _WizardLayout = ({ app }: WizzardLayoutProps) => {
  return (
    <AppProvider app={app}>
      <WizardLayoutContent />
    </AppProvider>
  )
}

export const WizardLayout = WithAuth(_WizardLayout)

export const useInsuranceParamsContext = () => {
  const context = useContext(insuranceParamsContext)

  if (context === undefined || Object.keys(context).length == 0) {
    throw new Error('useInsuranceParamsContext must be used within a insuranceParamsContext!')
  }

  return context
}